import { ErrorHandler, Injectable, inject } from '@angular/core';

import { FirebaseCrashlytics, StackFrame } from '@capacitor-firebase/crashlytics';
import { LoggingService } from './logging.service';
import { NotificationService } from './notification.service';
import { Platform } from '@ionic/angular';

@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
  loggingService = inject(LoggingService);
  notificationService = inject(NotificationService);
  platform = inject(Platform);

  handleError(error: any): void {
    //  just log it to the console and TrackJS will pick it up
    console.error(error);
    try {
      if (this.platform.is('android') || this.platform.is('ios')) {
        const message = error?.message ? String(error.message) : String(error);
        //  These reject rather than throw, and this handler must never fail: an unhandled rejection
        //  raised from inside Angular's ErrorHandler comes straight back here.
        FirebaseCrashlytics.log({ message }).catch(() => { });
        //  log() only leaves a breadcrumb for the next native crash. Since this handler catches the
        //  error, recordException is what actually reports it to the Crashlytics console.
        FirebaseCrashlytics
          .recordException({ message, stacktrace: this.toStacktrace(error) })
          .catch(() => { });
      }
    } catch (err) {
      console.log(error);
    }
  }

  /**
   * Convert an error's stack into the frame list the Crashlytics plugin expects.
   *
   * Both engines have to be handled: V8 (Android) writes `at fn (file:line:col)` after a header
   * line, while JavaScriptCore (iOS) writes `fn@file:line:col` with no header at all.
   */
  private toStacktrace(error: any): StackFrame[] {
    const stack: string = error?.stack;
    if (typeof stack !== 'string') return [];

    const frames: StackFrame[] = [];
    for (const line of stack.split('\n')) {
      const match = line.match(/^\s*(?:at\s+)?(?:(.*?)\s*[@(])?([^@(\s]+?):(\d+):\d+\)?\s*$/);
      //  Skips the V8 header line ("Error: message"), which cannot match.
      if (match) {
        frames.push({
          functionName: match[1] || '<anonymous>',
          fileName: match[2],
          lineNumber: Number(match[3]),
        });
      }
    }
    return frames;
  }

}
